import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { PageHeader, Loading } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Mail, Save, CreditCard, GraduationCap, BellRing, UserPlus, Send } from "lucide-react";

const META = {
  payment_confirmation: { icon: CreditCard, when: "Sent automatically when a candidate's payment is confirmed." },
  payment_confirmation_cpd: { icon: CreditCard, when: "Sent automatically when a CPD course payment is confirmed (tells the buyer their link will follow separately)." },
  cpd_course_link: { icon: GraduationCap, when: "Sent when you email a buyer their individual CPD course link from the Sales page." },
  course_access: { icon: Mail, when: "Sent for LIVE courses when a candidate is added manually, imported, booked via an order or by a member, or when you resend the access link from Sales." },
  course_access_cpd: { icon: GraduationCap, when: "Sent for ONLINE (non-live) courses when a learner is booked by a member, added manually or via an order - tells them their course link will follow shortly." },
  membership_welcome: { icon: UserPlus, when: "Sent automatically when someone starts a monthly membership." },
  membership_renewal: { icon: CreditCard, when: "For when a membership renews each month." },
  membership_cancellation: { icon: BellRing, when: "For when a membership is cancelled." },
  course_completion: { icon: GraduationCap, when: "Sent automatically when a learner completes a course." },
  payment_reminder: { icon: BellRing, when: "Sent when you trigger reminders for overdue / expiring training." },
  enrolment_confirmation: { icon: UserPlus, when: "Sent when a learner is registered for a course." },
};
const PLACEHOLDERS = ["{{name}}", "{{course}}", "{{access_link}}", "{{course_link}}", "{{portal_link}}", "{{date}}", "{{session_date}}", "{{session_time}}", "{{session_location}}", "{{join_link}}", "{{status}}", "{{platform}}"];

export default function Emails() {
  const [templates, setTemplates] = useState(null);

  const load = async () => { const { data } = await api.get("/email-templates"); setTemplates(data); };
  useEffect(() => { load(); }, []);

  if (!templates) return <Loading />;

  return (
    <div>
      <PageHeader title="Automated Emails" subtitle="Edit the emails sent automatically at each stage of the learner journey." testid="emails-header" />
      <div className="mb-5 rounded-lg border border-border bg-accent/30 p-4">
        <p className="text-sm font-medium">Available placeholders</p>
        <div className="flex flex-wrap gap-2 mt-2">
          {PLACEHOLDERS.map((p) => <code key={p} className="text-xs font-mono px-2 py-1 rounded bg-background border border-border">{p}</code>)}
        </div>
        <p className="text-xs text-muted-foreground mt-2">Insert these into the subject or body and they'll be replaced with the real values when the email is sent.</p>
      </div>
      <div className="space-y-5">
        {templates.map((t) => <TemplateCard key={t.stage} tpl={t} onSaved={load} />)}
      </div>
    </div>
  );
}

function TemplateCard({ tpl, onSaved }) {
  const [form, setForm] = useState(tpl);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const meta = META[tpl.stage] || { icon: Mail, when: "" };
  const Icon = meta.icon;

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/email-templates/${tpl.stage}`, { subject: form.subject, body: form.body, enabled: form.enabled });
      toast.success(`${form.name} saved`);
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post(`/email-templates/${tpl.stage}/test`, { subject: form.subject, body: form.body });
      toast.success(`Test email sent to ${data.to}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  return (
    <Card className="p-5" data-testid={`email-template-${tpl.stage}`}>
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><Icon className="h-5 w-5 text-primary" /></div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-heading font-semibold">{form.name}</h3>
            {form.enabled ? <Badge variant="outline" className="border-emerald-300 text-emerald-700 text-xs">Active</Badge> : <Badge variant="outline" className="text-muted-foreground text-xs">Off</Badge>}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">{meta.when}</p>
        </div>
        <Switch checked={form.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid={`email-enabled-${tpl.stage}`} />
      </div>
      <div className="mt-4 space-y-3">
        <div><Label>Subject</Label><Input value={form.subject} onChange={(e) => set("subject", e.target.value)} className="mt-1.5" data-testid={`email-subject-${tpl.stage}`} /></div>
        <div><Label>Body</Label><Textarea value={form.body} onChange={(e) => set("body", e.target.value)} rows={7} className="mt-1.5 font-mono text-sm" data-testid={`email-body-${tpl.stage}`} /></div>
      </div>
      <div className="flex justify-end gap-2 mt-3">
        <Button variant="outline" onClick={sendTest} disabled={testing} data-testid={`email-test-${tpl.stage}`}><Send className="h-4 w-4 mr-1.5" />{testing ? "Sending..." : "Send me a test"}</Button>
        <Button onClick={save} disabled={saving} data-testid={`email-save-${tpl.stage}`}><Save className="h-4 w-4 mr-1.5" />{saving ? "Saving..." : "Save"}</Button>
      </div>
    </Card>
  );
}
